import { FC, useMemo } from 'react';
import {
  AdditionalNavigation,
  Container,
  FollowUsBlock,
  FollowUsLinks,
  FooterContainer,
  FooterCopyrightWrapper,
  FooterWrapper,
  LinkStyled,
  LogoBlock,
  VersionContent,
} from './styles';
import { Link } from '@/config/i18n/navigation';
import { FacebookIcon, Hint, LinkedinIcon, LogoFooter, PinterestIcon } from '@/components';
import dayjs from 'dayjs';
import { COLORS, FACEBOOK_URL, FOOTER_ITEMS, LINKEDIN_URL, PINTEREST_URL } from '@/constants';
import { Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import { useGetAppVersion } from '@/queris';
import { useAppContext } from '@/contexts';
import { useIsMounted } from '@/hooks';

import packageJson from '@/../package.json';
import geoWidgetPackageJson from '@/../node_modules/@propileya/geo-widget/package.json';

export const Footer: FC = () => {
  const t = useTranslations();
  const { isStickyBarVisible } = useAppContext();

  const isMounted = useIsMounted();

  // FOOTER_ITEMS depends on isProd (runtimeConfig), which is only available on the client
  // Therefore, we render items only after mounting to avoid hydration errors
  const footerItems = useMemo(() => (isMounted ? FOOTER_ITEMS : []), [isMounted]);

  const facebookUrl = FACEBOOK_URL || '';
  const linkedinUrl = LINKEDIN_URL || '';
  const pinterestUrl = PINTEREST_URL || '';

  const { data } = useGetAppVersion();

  const versionValue = `Backend v${data?.version} · ${dayjs(data?.time).format('YYYY.MM.DD HH:mm')} / Fronted v${packageJson.version} · branch ${data?.branch} / Map version · ${geoWidgetPackageJson.version}`;
  return (
    <FooterWrapper $hasStickyBar={isStickyBarVisible}>
      <FooterContainer>
        <Container>
          <LogoBlock>
            <Link href="/" aria-label="Alleimmobilien — go to homepage">
              <LogoFooter aria-hidden="true" />
            </Link>
          </LogoBlock>
          <AdditionalNavigation>
            {footerItems.map((e, i) => (
              <LinkStyled href={e.link} key={i}>
                <Typography fontSize={14} fontWeight={600} lineHeight={'30px'}>
                  {t(e.label)}
                </Typography>
              </LinkStyled>
            ))}
          </AdditionalNavigation>
          <FollowUsBlock>
            <Typography
              fontSize={14}
              fontWeight={600}
              lineHeight={'30px'}
              color={COLORS['gray-light']}
            >
              {t('footer.followUs')}
            </Typography>
            <FollowUsLinks>
              <Hint message={t('footer.followUsOnFacebook')} placement="top">
                <LinkStyled
                  href={facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('footer.followUsOnFacebook')}
                >
                  <FacebookIcon aria-hidden="true" />
                </LinkStyled>
              </Hint>
              <Hint message={t('footer.followUsOnLinkedIn')} placement="top">
                <LinkStyled
                  href={linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('footer.followUsOnLinkedIn')}
                >
                  <LinkedinIcon aria-hidden="true" />
                </LinkStyled>
              </Hint>
              <Hint message={t('footer.followUsOnPinterest')} placement="top">
                <LinkStyled
                  href={pinterestUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('footer.followUsOnPinterest')}
                >
                  <PinterestIcon aria-hidden="true" />
                </LinkStyled>
              </Hint>
            </FollowUsLinks>
          </FollowUsBlock>
        </Container>
      </FooterContainer>
      <FooterCopyrightWrapper>
        <Container>
          <Typography
            fontWeight={700}
            fontSize={13}
            lineHeight={'32px'}
            color={COLORS['gray-light']}
          >
            <span>{`© ${dayjs().year()} ${t('footer.copyrightText')}`}</span>
          </Typography>
        </Container>
      </FooterCopyrightWrapper>
      {data && <VersionContent>{versionValue}</VersionContent>}
    </FooterWrapper>
  );
};
